import React from "react";
import { useActiveOption } from "../../hooks/useActiveOption.js";
import { useOpenLease } from "../../hooks/useOpenLease.js";
import { cn } from "../../lib/utils.js";
import { useOverlayPresence } from "../../primitives/OverlaySurface.js";
import {
  useDismissable,
  useLayer,
  usePosition,
} from "../../primitives/index.js";
import { PEOPLE_FIELD_LABELS } from "./people-field-labels.js";
import type {
  PeopleFieldProps,
  PeopleFieldValue,
} from "./people-field.types.js";
import { PeopleFieldPanel } from "./PeopleFieldPanel.js";
import { PeopleFieldTrigger } from "./PeopleFieldTrigger.js";
import { usePeopleFieldData } from "./usePeopleFieldData.js";

/**
 * One field and one list for every place that picks a person (assign-person
 * consistency; ADR-0092 amendment of 2026-10-01). Closed, it is the picked
 * person's chip — avatar, name and access tag, × to clear — or a ghost
 * "Assign person" button. Open, a search input over "Assign to me", the
 * people with access (from memory) and, from two letters, the whole
 * workspace. Data-blind: who has access, who the workspace holds and whether
 * the caller may add someone all come from `source`.
 *
 * Typing never changes the value: only a pick, the typed-name row or ×
 * calls `onChange`. Escape and Tab close the list and give focus back to the
 * trigger, and so does a pick.
 */
export function PeopleField({
  value,
  onChange,
  source,
  label,
  mode = "single",
  emptyLabel = "Assign person",
  allowTyped = false,
  grantTiming = "now",
  pendingGrant = false,
  size = "md",
  readOnly = false,
  disabled = false,
  id,
  onFocus,
  onBlur,
  labels: overrides,
}: PeopleFieldProps): React.ReactElement {
  const labels = React.useMemo(
    () => ({ ...PEOPLE_FIELD_LABELS, ...overrides }),
    [overrides],
  );
  const autoId = React.useId();
  const baseId = id ?? autoId;
  const ids = {
    dialog: `${baseId}-dialog`,
    input: `${baseId}-search`,
    list: `${baseId}-list`,
    option: (index: number) => `${baseId}-option-${index}`,
  };
  const { open, openList, closeList } = useOpenLease({
    disabled: disabled || readOnly,
    onFocus,
    onBlur,
  });
  const [query, setQuery] = React.useState("");
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const surfaceRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const focusTriggerNext = React.useRef(false);
  /* ADR-0091: the list leaves rather than vanishing. It stays mounted, inert
     and unannounced through its exit, and what it shows stays as it was —
     the search and the query keep running until it is gone. */
  const { present, closing, motionProps } = useOverlayPresence(open, {
    ref: surfaceRef,
  });

  const { roster, model, view, search } = usePeopleFieldData({
    source,
    value,
    query,
    present,
    mode,
    allowTyped,
    grantTiming,
    pendingGrant,
    labels,
  });

  const layerId = useLayer({ open });
  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: rovingKeyDown,
  } = useActiveOption({
    itemCount: model.items.length,
    resetKey: query,
    open,
    listRef,
  });
  const position = usePosition({
    // `present`, not `open`: the list keeps its place while it fades out.
    open: present,
    triggerRef: rootRef,
    contentRef: surfaceRef,
    align: "start",
    sideOffset: 6,
  });
  const positioned = position !== null;

  const openFresh = (): void => {
    setQuery("");
    // The query may already be "", which would not re-run the reset, so a
    // reopened list would keep the last highlighted row for Enter.
    setActiveIndex(0);
    openList();
  };
  const close = (returnFocus: boolean): void => {
    closeList();
    if (returnFocus) triggerRef.current?.focus();
  };
  const insideRefs = React.useMemo(() => [rootRef, surfaceRef], []);
  useDismissable({
    layerId,
    open,
    insideRefs,
    onDismiss: (reason) => close(reason === "escape"),
  });

  // A list still hidden for measuring cannot take focus, so the input takes
  // it once the list is placed.
  React.useLayoutEffect(() => {
    if (open && positioned) inputRef.current?.focus({ preventScroll: true });
  }, [open, positioned]);

  // A pick or × gives focus back to the trigger, which by then may be another
  // element: the empty button becomes a chip, and a chip the empty button.
  React.useLayoutEffect(() => {
    if (!focusTriggerNext.current) return;
    focusTriggerNext.current = false;
    triggerRef.current?.focus();
  });

  const commit = (index: number): void => {
    // A list on its way out must not pick somebody.
    if (closing) return;
    const item = model.items[index];
    if (!item) return;
    if (item.kind === "loadMore") {
      search.loadNextPage();
      return;
    }
    if (item.kind === "person" && item.disabled) return;
    const next: PeopleFieldValue =
      item.kind === "typed"
        ? { kind: "typed", name: item.text }
        : { kind: "person", id: item.person.id, name: item.person.name };
    focusTriggerNext.current = true;
    close(false);
    onChange(next);
  };
  const clear = (): void => {
    focusTriggerNext.current = true;
    if (open) close(false);
    onChange(null);
  };

  /* Tab closes the list as it leaves it: from the input, unless Retry
     follows it, and from Retry, the last control. */
  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      rovingKeyDown(event);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      commit(activeIndex);
      return;
    }
    if (event.key === "Tab" && (event.shiftKey || model.status !== "error")) {
      event.preventDefault();
      close(true);
    }
  };
  const onRetryKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab" || event.shiftKey) return;
    event.preventDefault();
    close(true);
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex min-w-0 items-center",
        size === "md" ? "min-h-9" : "min-h-7",
      )}
    >
      <PeopleFieldTrigger
        view={view}
        seed={value?.kind === "person" ? value.id : undefined}
        label={label}
        emptyLabel={emptyLabel}
        labels={labels}
        size={size}
        open={open}
        triggerId={baseId}
        popupId={ids.dialog}
        readOnly={readOnly}
        disabled={disabled}
        triggerRef={triggerRef}
        onToggle={() => (open ? close(false) : openFresh())}
        onOpenByKey={() => {
          if (!open) openFresh();
        }}
        onClear={clear}
      />
      <PeopleFieldPanel
        present={present}
        motionProps={motionProps}
        position={position}
        surfaceRef={surfaceRef}
        inputRef={inputRef}
        listRef={listRef}
        ids={ids}
        dialogLabel={labels.dialogName(label)}
        labels={labels}
        query={query}
        onQueryChange={setQuery}
        onInputKeyDown={onInputKeyDown}
        onRetryKeyDown={onRetryKeyDown}
        model={model}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onPick={commit}
        loading={search.loading}
        loadingMore={search.loadingMore}
        loadMoreFailed={search.loadMoreFailed}
        // A place with no access list shows no roster rows, so a failed
        // search must not say "People with access still show".
        hasRoster={source.grantMode.kind !== "none" && roster.length > 0}
        onRetry={search.retry}
      />
    </div>
  );
}
